import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import GetAppIcon from '@material-ui/icons/GetApp';
import SendIcon from '@material-ui/icons/Send';

import { Formik } from 'formik';
import { Button, FilledInput, FormControl, Grid, InputLabel, makeStyles } from '@material-ui/core';

import DialogComponent from 'app/components/DialogComponent';
import SaveBox from '../../components/SaveBox';
import Loading from 'app/layouts/Loading';
import { downloadFile } from 'app/utils/forms/files';
import { errorHandler } from 'app/utils/errorHandler';
import lineEndpoint from 'app/api/services/lineEndpoint';
import { Alert } from '@material-ui/lab';
import helpers from '../../../../utils/helpers';
import notification from '../../../../config/notification';
import { STATUS_CODES } from '../../../../config/status';
import RequestProcess from '../../components/RequestProcess';
import { SERVICES_CODES } from '../../../../config/services';

function ConductorModal(props) {
  const classes = useStyles();
  const { open, onClose, data, onUpdate, cdLinha } = props;

  const servicesFilterModal = useRef([
    { cdServico: SERVICES_CODES.SLIC_INCLUSAO_CONDUTOR_SUBSTITUTO },
    { cdServico: SERVICES_CODES.SLIC_TRANSFERENCIA_PERMISSAO },
    { cdServico: SERVICES_CODES.SLIC_CANCELAMENTO_PERMISSAO }
  ]).current;

  const [loading, setLoading] = useState(false);
  const [loadingProcess, setLoadingProcess] = useState(false);
  const [nuCondutorFmt, setNuCondutorFmt] = useState('');
  const [deCpf, setDeCpf] = useState('');
  const [deNome, setDeNome] = useState('');
  const [idFile, setIdFile] = useState('');
  const [processId, setProcessId] = useState(null);
  const [processNumber, setProcessNumber] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [certificateId, setCertificateId] = useState(null);
  const [requestNumber, setRequestNumber] = useState(null);
  const [requestSituation, setRequestSituation] = useState(0);
  const [requestProcessValues, setRequestProcessValues] = useState({});
  const [initialValueRequestProcess, setInitialValueRequestProcess] = useState({});

  const dispatch = useDispatch();

  const canSendToSignature = useMemo(() => {
    if (requestId) {
      if (!(requestProcessValues.cdProcesso && requestProcessValues.cdProcesso !== '')
        && !(requestProcessValues.cdSolicitacao && requestProcessValues.cdSolicitacao !== '')
        && requestSituation !== STATUS_CODES.EM_ANALISE
        && requestSituation !== STATUS_CODES.AGUARDANDO_AJUSTES_DOCUMENTO
        && requestSituation !== STATUS_CODES.RETORNO_DIRETOR_GERAL
        && requestSituation !== STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS) {
        return false;
      }
    }
    return (processId || requestId);
  }, [requestProcessValues, processId, requestId, requestSituation]);

  useEffect(() => {
    (() => {
      if (data.conductor) {
        setIdFile(data.conductor.idFile);
        setDeCpf(data.conductor.deCpf);
        setDeNome(data.conductor.deNome);
        setCertificateId(data.conductor.cdCertificado);
        if (data.conductor.nuCondutor && data.conductor.nuCondutor !== '') {
          setNuCondutorFmt(data.conductor.nuCondutorFmt ||
            `${data.conductor.nuCondutor.toString().padStart(3, '0')}/${data.conductor.nuAno}`);
        }
        setInitialValueRequestProcess({
          cdSolicitacao: data.conductor.cdSolicitacaoAssinatura,
          nuSolicitacao: data.conductor.nuSolicitacaoAssinatura,
          cdProcesso: data.conductor.cdProcessoAssinatura,
          nuProcesso: data.conductor.nuProcessoAssinatura
        })
      }
      if (data.requestProcessValues) {
        setRequestSituation(data.requestProcessValues.cdSituacao);
      }
    })();
  }, [data]);

  useEffect(() => {
    (() => {
      if ((requestProcessValues.cdProcesso && requestProcessValues.cdProcesso !== '') ||
        (requestProcessValues.cdSolicitacao && requestProcessValues.cdSolicitacao !== '')){
        setProcessId(requestProcessValues.cdProcesso);
        setRequestId(requestProcessValues.cdSolicitacao);
        setProcessNumber(requestProcessValues.nuProcesso);
        setRequestNumber(requestProcessValues.nuSolicitacaoFmt);
      } else if (data.conductor) {
        setProcessId(data.conductor.cdProcesso);
        setRequestId(data.conductor.cdSolicitacao);
        setProcessNumber(data.conductor.nuProcesso);
        setRequestNumber(data.conductor.nuSolicitacaoFmt);
      }
    })();
  },[requestProcessValues, data]);

  const handleCancel = () => {
    onClose();
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      if (data?.conductor?.cdCondutorSubstituto) {
        const response = await lineEndpoint.generateConductorFile(
          data.conductor.cdCondutorSubstituto,
        );
        if (response && response.status === 200 && response.data) {
          notification('Documento gerado com sucesso', { toastId: 'VALIDATION', type: 'success' });
          const conductorData = {
            ...data.conductor,
            ...response.data,
            idFile: response.data.idFileCrc,
          };
          setCertificateId(response.data.cdCertificado);
          setNuCondutorFmt(response.data.condutorSubstituto?.nuCondutorFmt);
          onUpdate(conductorData);
          setIdFile(response.data.idFileCrc);
        }
      } else {
        notification('Condutor substituto não identificado', {
          toastId: 'VALIDATION',
          type: 'error',
        });
      }
    } catch (error) {
      errorHandler(error.response?.status, error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const sendDocument = async () => {
    if (!data.conductor) {
      notification('Erro ao identificar condutor substituto', {
        toastId: 'VALIDATION',
        type: 'error',
      });
      return;
    }
    setLoading(true);
    let response = null;
    let destinationMsg = '';
    if (requestId && requestId !== '') {
      response = await lineEndpoint.sendConductorsToRequest({
        cdCertificado: certificateId,
        cdSolicitacao: requestId
      });
      destinationMsg = `na solicitação ${requestNumber ? requestNumber : ''}`;
    } else if (processId && processId !== '') {
      response = await lineEndpoint.sendConductorsToProcess({
        cdCertificado: certificateId,
        nuProcesso: processNumber,
        cdProcesso: processId
      });
      destinationMsg = `no processo ${processNumber ? processNumber : ''}`;
    }
    if (!response || response.status !== 200) {
      notification('Erro ao encaminhar documento do condutor substituto', {
        toastId: 'VALIDATION_ERROR',
        type: 'error',
      });
      setLoading(false);
      return;
    }
    notification(`Carteira de condutor incluída ${destinationMsg} com sucesso`, {
      toastId: 'VALIDATION_SUCESS',
      type: 'success',
    });
    setLoading(false);
    onClose();
  };

  const handleBlurProcess = (value) => {
    setLoadingProcess(true);
  }

  const handleSetRequestProcessValues = (value) => {
    setLoadingProcess(false);
    setRequestProcessValues(value);
  }

  return (
    <DialogComponent
      title={'Emissão de Carteira de Condutor Substituto'}
      subtitle={''}
      open={open}
      onClose={onClose}
    >
      <>
        {loading && <Loading />}
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Alert severity='info'>
              Antes de emitir o documento, certifique-se de que todas as informações do condutor
              substituto foram salvas!
            </Alert>
          </Grid>

          <Formik initialValues={initialValueRequestProcess} onSubmit={() => {}}>
            {formProps => {
              return (
                <RequestProcess
                  {...props}
                  {...formProps}
                  initialState={initialValueRequestProcess}
                  canEdit={true}
                  cdLinha={cdLinha}
                  servicesFilter={servicesFilterModal}
                  requestProcessValues={requestProcessValues}
                  setRequestProcessValues={handleSetRequestProcessValues}
                  setRequestChanged={() => {}}
                  fieldWidth={6}
                  handleBlur={handleBlurProcess}
                />
              );
            }}
          </Formik>

          <Grid item xs={2}>
            <FormControl variant='filled' fullWidth>
              <InputLabel htmlFor='filled-adornment-password'>Nº Carteira</InputLabel>
              <FilledInput type={'text'} value={nuCondutorFmt} margin='dense' size='small' disabled />
            </FormControl>
          </Grid>
          <Grid item xs={5}>
            <FormControl variant='filled' fullWidth>
              <InputLabel htmlFor='filled-adornment-password'>
                CPF do condutor substituto
              </InputLabel>
              <FilledInput
                type={'text'}
                value={helpers.formatarDocumento(deCpf ? deCpf : '')}
                margin='dense'
                size='small'
                disabled
              />
            </FormControl>
          </Grid>
          <Grid item xs={5}>
            <FormControl variant='filled' fullWidth>
              <InputLabel htmlFor='filled-adornment-password'>
                Nome do condutor substituto
              </InputLabel>
              <FilledInput type={'text'} value={deNome} margin='dense' size='small' disabled />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            {idFile && idFile !== '' && (
              <>
                <Button
                  color='secondary'
                  className={classes.button}
                  startIcon={<GetAppIcon />}
                  onClick={async () => await downloadFile({ idFile, dispatch })}
                >
                  Exibir
                </Button>
                {canSendToSignature && (
                  <Button
                    color='primary'
                    className={classes.button}
                    startIcon={<SendIcon />}
                    onClick={() => sendDocument()}
                  >
                    Encaminhar
                  </Button>
                )}
              </>
            )}
          </Grid>

          <Grid item xs={12}>
            <SaveBox
              onCancel={handleCancel}
              onSave={handleGenerate}
              onSaveColor={'secondary'}
              middleButtonLabel={'Gerar'}
              middleButtonDisabled={loadingProcess}
              onSaveStyles={{ color: '#fff' }}
              removeSpacing={true}
            />
          </Grid>
        </Grid>
      </>
    </DialogComponent>
  );
}

const useStyles = makeStyles(theme => ({
  button: {
    marginRight: '20px',
  },
}));

export default ConductorModal;
